import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { scheduleAppointments } from '@provider-directory/app/actions/schedule-appointment';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import { getSchedulingFlowType } from '@provider-directory/contexts/DecisionTreeContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { useSharedDecisionTree } from '@provider-directory/contexts/SharedDecisionTreeContext';

interface ScheduleAppointmentHook {
  schedule: (providerId: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const useScheduleAppointment = (): ScheduleAppointmentHook => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const { appointment, updateAppointment } = useAppointment();
  const analytics = useAnalytics();
  const { provider } = useProvider();
  const { clearSharedData } = useSharedDecisionTree();

  const schedule = async (ehrExternalId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const scheduledAppointment = await scheduleAppointments(
        appointment,
        ehrExternalId,
      );

      if (scheduledAppointment.status !== 'ok') {
        analytics.apptBookedErrorEvent({
          provider,
          appointment,
          schedulingFlowType: getSchedulingFlowType(),
          linkContextPage: 'SchedulingReview',
          linkContextComponent: 'AppointmentSummary',
          errorCode:
            scheduledAppointment.status === 'validation-error'
              ? scheduledAppointment.errorCode
              : scheduledAppointment.status,
        });
        if (
          scheduledAppointment.status === 'validation-error' &&
          scheduledAppointment.errorCode
        ) {
          setError(scheduledAppointment.errorCode);
        } else {
          setError(scheduledAppointment.status);
        }
      } else {
        analytics.apptBookedSuccessEvent({
          provider,
          appointment,
          schedulingFlowType: getSchedulingFlowType(),
          linkContextPage: 'SchedulingReview',
          linkContextComponent: 'AppointmentSummary',
          appointmentId: scheduledAppointment.appointment?.appointmentId,
        });
        updateAppointment({
          duration: scheduledAppointment.appointment?.duration,
          patientInstructions:
            scheduledAppointment.appointment?.patientInstructions ?? [],
        });
        clearSharedData();
        router.replace('confirm');
      }
    } catch {
      setError('scheduling-error');
    } finally {
      setIsLoading(false);
    }
  };

  return { schedule, isLoading, error };
};
